// Copied from apps/admin/src/features/map-view/model/helpers/photo-points.ts (PRD #875).
import { ConnectProto } from "@racoon/api";
import { toDateLike } from "@racoon/lib";
import { pickupLocationPolygons } from "../geofence-polygons";

type PhotoShot = {
  type: ConnectProto.FileUploadType;
  takenAt: Date | null;
};

export type PhotoPoint = {
  lat: number;
  lon: number;
  jobId: string;
  job: ConnectProto.Job;
  siteName?: string;
  /** Where the distance is measured to; absent inside the geofence or without a site. */
  site?: { lat: number; lon: number };
  /** Metres to the nearer of the site's geofence (0 inside) and its point. */
  siteDistanceM: number | null;
  shots: PhotoShot[];
};

/**
 * How far off the site a photo may be before it is flagged. The same ~50 m AIS
 * draws around a site: a ТБО geofence is often just the container pad, which
 * the truck itself can never stand inside.
 */
export const PHOTO_OFF_SITE_M = 50;

export const isPhotoOffSite = (point: PhotoPoint): boolean =>
  point.siteDistanceM !== null && point.siteDistanceM > PHOTO_OFF_SITE_M;

const M_PER_DEG_LAT = 110_574;

type SiteNearest = { distanceM: number; lat: number; lon: number };

/**
 * The nearest of the site's geofence and its point. They can mark different places
 * (the containers vs the entrance), and a photo at either is on site.
 */
export function nearestOnSite(
  lat: number,
  lon: number,
  site: { lat?: number; lon?: number; geometry?: unknown },
): SiteNearest | null {
  // A local flat projection: sites are metres across.
  const mPerDegLon = 111_320 * Math.cos((lat * Math.PI) / 180);
  const toXY = ([pLat, pLon]: [number, number]): [number, number] => [
    (pLon - lon) * mPerDegLon,
    (pLat - lat) * M_PER_DEG_LAT,
  ];
  const fromXY = ([x, y]: [number, number], distanceM: number): SiteNearest => ({
    distanceM,
    lat: lat + y / M_PER_DEG_LAT,
    lon: lon + x / mPerDegLon,
  });

  let nearest: [number, number] = [Infinity, Infinity];
  if (site.lat && site.lon) nearest = toXY([site.lat, site.lon]);

  const polygons = pickupLocationPolygons(site.geometry);
  for (const [outline, ...holes] of polygons) {
    const rings = [outline, ...holes].map((ring) => ring.map(toXY));
    for (const ring of rings) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const onSegment = nearestOnSegment(...ring[j], ...ring[i]);
        if (Math.hypot(...onSegment) < Math.hypot(...nearest)) nearest = onSegment;
      }
    }
    if (containsOrigin(rings[0]) && !rings.slice(1).some(containsOrigin)) {
      return fromXY([0, 0], 0);
    }
  }
  const distanceM = Math.hypot(...nearest);
  return Number.isFinite(distanceM) ? fromXY(nearest, distanceM) : null;
}

/** Ray-casting point-in-polygon for the origin. */
function containsOrigin(ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > 0 !== yj > 0 && (xj - xi) * (-yi / (yj - yi)) + xi > 0) inside = !inside;
  }
  return inside;
}

/** The point of segment (x1,y1)-(x2,y2) nearest the origin. */
function nearestOnSegment(x1: number, y1: number, x2: number, y2: number): [number, number] {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(x1 * dx + y1 * dy) / len2));
  return [x1 + t * dx, y1 + t * dy];
}

/**
 * Where each job's photos were taken, one point per distinct position per job.
 * A service start and end photo usually share a GPS fix, and two markers on
 * one spot would hide each other's tooltip.
 */
export function photoPointsForJobs(jobs: ConnectProto.Job[]): PhotoPoint[] {
  const points: PhotoPoint[] = [];
  for (const job of jobs) {
    const byPosition = new Map<string, PhotoPoint>();
    for (const photo of job.fileReports ?? []) {
      // An unset coordinate decodes as 0, and a 0 from the phone is a failed fix.
      if (!photo.lat || !photo.lon) continue;
      const key = `${photo.lat},${photo.lon}`;
      let point = byPosition.get(key);
      if (!point) {
        const site = job.pickupLocation;
        const nearest = site ? nearestOnSite(photo.lat, photo.lon, site) : null;
        point = {
          lat: photo.lat,
          lon: photo.lon,
          jobId: String(job.id),
          job,
          siteName: site?.name,
          site: nearest?.distanceM ? { lat: nearest.lat, lon: nearest.lon } : undefined,
          siteDistanceM: nearest?.distanceM ?? null,
          shots: [],
        };
        byPosition.set(key, point);
        points.push(point);
      }
      point.shots.push({ type: photo.type, takenAt: toDateLike(photo.createdAt) });
    }
  }
  for (const point of points) {
    point.shots.sort((a, b) => (a.takenAt?.getTime() ?? 0) - (b.takenAt?.getTime() ?? 0));
  }
  return points;
}

/** The shots taken by `time`, for revealing photos along with a playback. */
export function photoPointsUpTo(points: PhotoPoint[], time: number): PhotoPoint[] {
  const out: PhotoPoint[] = [];
  for (const point of points) {
    // A shot with no time cannot be placed on the timeline, so it is never hidden.
    const shots = point.shots.filter((s) => !s.takenAt || s.takenAt.getTime() <= time);
    if (shots.length === point.shots.length) out.push(point);
    else if (shots.length) out.push({ ...point, shots });
  }
  return out;
}
